// MonthlyUpdateSection.jsx - Sección de actualización mensual en Dashboard
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { FaCalendarAlt, FaWeight, FaHeartbeat, FaUtensils, FaStar, FaCheck, FaSpinner, FaClock, FaHistory } from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL;

const COMMON_PATHOLOGIES = [
  'Ninguna nueva', 'Diabetes', 'Hipertensión', 'Hipotiroidismo', 'Hipertiroidismo',
  'Síndrome de ovario poliquístico', 'Resistencia a la insulina',
  'Colesterol alto', 'Ansiedad', 'Problemas digestivos', 'Otra'
];

const MonthlyUpdateSection = ({ userEmail }) => {
  const [canUpdate, setCanUpdate] = useState(false);
  const [daysUntilNext, setDaysUntilNext] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  
  const [formData, setFormData] = useState({
    current_weight: '',
    new_pathologies: [],
    skip_pathologies: false,
    foods_to_add: '',
    foods_to_remove: '',
    diet_rating: 5,
    workout_rating: 5,
    comments: ''
  });

  useEffect(() => {
    if (userEmail) {
      checkCanUpdate();
    }
  }, [userEmail]);

  const checkCanUpdate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/user/can-do-monthly-update/${userEmail}`);
      setCanUpdate(response.data.can_update);
      setDaysUntilNext(response.data.days_until_next);
      setLastUpdate(response.data.last_update);
    } catch (error) {
      console.error('Error checking monthly update:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/api/user/monthly-updates-history/${userEmail}`);
      setHistory(response.data.updates || []);
      setShowHistory(true);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Error al cargar historial');
    }
  };

  const handlePathologyToggle = (pathology) => {
    if (pathology === 'Ninguna nueva') {
      setFormData({ 
        ...formData, 
        new_pathologies: ['Ninguna nueva'],
        skip_pathologies: true
      });
    } else {
      const current = formData.new_pathologies.filter(p => p !== 'Ninguna nueva');
      if (current.includes(pathology)) {
        setFormData({ 
          ...formData, 
          new_pathologies: current.filter(p => p !== pathology),
          skip_pathologies: false
        });
      } else {
        setFormData({ 
          ...formData, 
          new_pathologies: [...current, pathology],
          skip_pathologies: false
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.current_weight || formData.current_weight <= 0) {
      toast.error('Por favor ingresa tu peso actual');
      return;
    }
    
    try {
      setSaving(true);
      
      const dataToSend = {
        current_weight: parseFloat(formData.current_weight),
        new_pathologies: formData.skip_pathologies ? [] : formData.new_pathologies.filter(p => p !== 'Ninguna nueva'),
        skip_pathologies: formData.skip_pathologies,
        foods_to_add: formData.foods_to_add ? formData.foods_to_add.split(',').map(s => s.trim()).filter(s => s) : [],
        foods_to_remove: formData.foods_to_remove ? formData.foods_to_remove.split(',').map(s => s.trim()).filter(s => s) : [],
        diet_rating: formData.diet_rating,
        workout_rating: formData.workout_rating,
        comments: formData.comments || null
      };
      
      await axios.post(`${API}/api/user/monthly-update/${userEmail}`, dataToSend);
      
      toast.success('¡Actualización mensual enviada! Tu entrenadora la revisará pronto.');
      setShowForm(false);
      checkCanUpdate();
      
      // Resetear formulario
      setFormData({
        current_weight: '',
        new_pathologies: [],
        skip_pathologies: false,
        foods_to_add: '',
        foods_to_remove: '',
        diet_rating: 5,
        workout_rating: 5,
        comments: ''
      });
    } catch (error) {
      console.error('Error submitting update:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar actualización');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CO', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700">
        <CardContent className="p-6 text-center">
          <FaSpinner className="animate-spin text-pink-500 text-2xl mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-pink-500/30 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500" />
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-pink-500/20">
                <FaCalendarAlt className="text-pink-400 text-xl" />
              </div>
              <div>
                <CardTitle className="text-white">Actualización Mensual</CardTitle>
                <CardDescription className="text-gray-400">
                  Ayúdanos a ajustar tu plan con tu progreso
                </CardDescription>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={loadHistory}
              className="border-gray-600 text-gray-400 hover:text-white"
            >
              <FaHistory className="mr-2" /> Historial
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {canUpdate ? (
            <>
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <p className="text-green-400 flex items-center gap-2">
                  <FaCheck /> ¡Tu actualización mensual está disponible!
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Cuéntanos cómo te fue este mes para personalizar mejor tu próximo plan.
                </p>
              </div>
              <Button 
                onClick={() => setShowForm(true)}
                className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
              >
                Completar mi actualización mensual
              </Button>
            </>
          ) : (
            <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
              <div className="flex items-center gap-3">
                <FaClock className="text-yellow-400 text-xl" />
                <div>
                  <p className="text-gray-300">
                    Tu próxima actualización estará disponible en <strong className="text-yellow-400">{daysUntilNext} días</strong>
                  </p>
                  {lastUpdate && (
                    <p className="text-gray-500 text-sm">
                      Última actualización: {formatDate(lastUpdate)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal del formulario */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent 
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(var(--brand-primary-glow),0.3)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FaCalendarAlt className="text-pink-500" />
              Actualización Mensual
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Cuéntanos cómo te fue este mes para ajustar tu plan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Peso actual */}
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <FaWeight className="text-cyan-400" /> Peso actual (kg) *
              </Label>
              <Input
                type="number"
                step="0.1"
                value={formData.current_weight}
                onChange={(e) => setFormData({ ...formData, current_weight: e.target.value })}
                placeholder="Ej: 65.5"
                className="bg-white/10 border-white/20 text-white"
              />
            </div>

            {/* Nuevas patologías */}
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <FaHeartbeat className="text-red-400" /> ¿Alguna condición de salud nueva?
              </Label>
              <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                {COMMON_PATHOLOGIES.map(pathology => (
                  <Badge
                    key={pathology}
                    variant="outline"
                    className={`cursor-pointer transition-all ${
                      formData.new_pathologies.includes(pathology)
                        ? pathology === 'Ninguna nueva'
                          ? 'bg-green-500/30 border-green-500 text-green-300'
                          : 'bg-red-500/30 border-red-500 text-red-300'
                        : 'border-white/20 text-gray-400 hover:border-pink-500/50'
                    }`}
                    onClick={() => handlePathologyToggle(pathology)}
                  >
                    {pathology}
                  </Badge>
                ))}
              </div>
              <p className="text-gray-500 text-xs">
                Selecciona "Ninguna nueva" si no hay cambios
              </p>
            </div>

            {/* Alimentos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <FaUtensils className="text-green-400" /> Alimentos a agregar
                </Label>
                <Textarea
                  value={formData.foods_to_add}
                  onChange={(e) => setFormData({ ...formData, foods_to_add: e.target.value })}
                  placeholder="Ej: quinoa, tofu, lentejas"
                  className="bg-white/10 border-white/20 text-white"
                  rows={2}
                />
                <p className="text-gray-500 text-xs">Separa con comas</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <FaUtensils className="text-orange-400" /> Alimentos a retirar
                </Label>
                <Textarea
                  value={formData.foods_to_remove}
                  onChange={(e) => setFormData({ ...formData, foods_to_remove: e.target.value })}
                  placeholder="Ej: brócoli, atún, huevo"
                  className="bg-white/10 border-white/20 text-white"
                  rows={2}
                />
                <p className="text-gray-500 text-xs">Separa con comas</p>
              </div>
            </div>

            {/* Calificaciones */}
            <div className="space-y-4">
              <Label className="text-gray-300 flex items-center gap-2">
                <FaStar className="text-yellow-400" /> ¿Cómo te fue con tu plan?
              </Label>
              
              {/* Dieta */}
              <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="text-gray-300 mb-3">Plan de alimentación: <span className="text-pink-400 font-bold">{formData.diet_rating}/10</span></p>
                <div className="flex justify-between gap-1">
                  {[1,2,3,4,5,6,7,8,9,10].map(num => (
                    <button
                      key={num}
                      onClick={() => setFormData({ ...formData, diet_rating: num })}
                      className={`flex-1 py-2 text-sm rounded transition-all ${
                        formData.diet_rating >= num
                          ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                          : 'bg-white/10 text-gray-500 hover:bg-white/20'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>No me gustó</span>
                  <span>¡Excelente!</span>
                </div>
              </div>

              {/* Rutina */}
              <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="text-gray-300 mb-3">Rutina de entrenamiento: <span className="text-cyan-400 font-bold">{formData.workout_rating}/10</span></p>
                <div className="flex justify-between gap-1">
                  {[1,2,3,4,5,6,7,8,9,10].map(num => (
                    <button
                      key={num}
                      onClick={() => setFormData({ ...formData, workout_rating: num })}
                      className={`flex-1 py-2 text-sm rounded transition-all ${
                        formData.workout_rating >= num
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                          : 'bg-white/10 text-gray-500 hover:bg-white/20'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Muy difícil</span>
                  <span>¡Perfecta!</span>
                </div>
              </div>
            </div>

            {/* Comentarios */}
            <div className="space-y-2">
              <Label className="text-gray-300">Comentarios adicionales (opcional)</Label>
              <Textarea
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                placeholder="¿Algo más que quieras contarnos sobre tu experiencia este mes?"
                className="bg-white/10 border-white/20 text-white"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-gray-600">
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving}
              className="bg-gradient-to-r from-pink-600 to-purple-600"
            >
              {saving ? (
                <>
                  <FaSpinner className="animate-spin mr-2" /> Enviando...
                </>
              ) : (
                <>
                  <FaCheck className="mr-2" /> Enviar actualización
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal del historial */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent 
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FaHistory className="text-cyan-400" /> Historial de Actualizaciones
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {history.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No hay actualizaciones previas</p>
            ) : (
              history.map((update, index) => (
                <Card key={index} className="bg-white/5 border-white/10">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-gray-400 text-sm">
                        {formatDate(update.created_at)}
                      </span>
                      {update.reviewed_by_admin ? (
                        <Badge className="bg-green-500/20 text-green-400">Revisado</Badge>
                      ) : (
                        <Badge className="bg-yellow-500/20 text-yellow-400">Pendiente</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Peso:</span>
                        <span className="text-white ml-2">{update.current_weight} kg</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Dieta:</span>
                        <span className="text-pink-400 ml-2">{update.diet_rating}/10</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Rutina:</span>
                        <span className="text-cyan-400 ml-2">{update.workout_rating}/10</span>
                      </div>
                      {update.new_pathologies?.length > 0 && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Nuevas condiciones:</span>
                          <span className="text-white ml-2">{update.new_pathologies.join(', ')}</span>
                        </div>
                      )}
                    </div>
                    {update.comments && (
                      <p className="text-gray-400 text-sm mt-3 italic">"{update.comments}"</p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MonthlyUpdateSection;
